import { useQuery } from "@apollo/client";
import UserAvatar from "components/User/UserAvatar";
import { LiquidPill } from "components/ui/v2";
import { useVendor } from "context/vendor/vendor.provider";
import gqlBuySell from "gql/BuySell";
import { ShoppingBag } from "lucide-react";
import useTranslation from "next-translate/useTranslation";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { IBuySellItem } from "types/BuySell.type";

import SideCard from "../SideCard/SideCard";
import SideCardV2 from "../SideCard/SideCardV2";

interface BuySellWidgetProps {
  readonly variant?: "default" | "v2";
}

/**
 * Widget "En venta" para el side panel de Explore.
 *
 * Mismo shape visual que `RecognitionCorporateValueRankingWidget`:
 *  · variante v2 muestra UN item por vez, centrado, con imagen grande +
 *    título + pill de precio + condition + seller.
 *  · Rotación automática cada 4200ms si hay más de un item.
 *  · Comparte las keyframes `explore-ranking-*` con el widget de ranking
 *    (se declaran globalmente vía styled-jsx; declarar la misma regla dos
 *    veces es idempotente).
 *  · CTA "Ver todos" navega a `/app/buy-sell`.
 *  · Se auto-oculta si no hay items disponibles.
 *
 * Nota sobre `fetchPolicy: "no-cache"`:
 *   ApolloClient está con `addTypename: false`. La query usa el fragment
 *   `on BuySellItem` que requiere typename para resolverse desde cache.
 *   Con `cache-first` los items llegan como `{}`. `no-cache` bypassea el
 *   cache y usa la respuesta cruda del server. Mismo approach que
 *   `BuySellPageV2`.
 */
const BuySellWidget = (props: BuySellWidgetProps) => {
  const { variant = "default" } = props;
  const { t } = useTranslation("common");
  const { vendor } = useVendor();
  const router = useRouter();
  const isEnabled = vendor?.enableBuySell === true;

  const { data, loading } = useQuery<{
    getBuySellItemsPaginated: {
      items: IBuySellItem[];
      total: number;
      totalPages: number;
    };
  }>(gqlBuySell.queries.GET_BUY_SELL_ITEMS_PAGINATED, {
    variables: {
      getBuySellItemsPaginatedInput: {
        limit: 10,
        offset: 1,
      },
    },
    fetchPolicy: "no-cache",
    skip: !isEnabled,
  });

  const availableItems: IBuySellItem[] = useMemo(() => {
    const items = data?.getBuySellItemsPaginated?.items ?? [];
    return items.filter((item) => {
      if (!item?.id || !item?.title) return false;
      const st = (item.status ?? "").toString().toUpperCase();
      return st === "" || st === "AVAILABLE";
    });
  }, [data]);

  const total = availableItems.length;

  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (activeIndex > Math.max(total - 1, 0)) {
      setActiveIndex(0);
    }
  }, [activeIndex, total]);

  const goNext = useCallback(() => {
    setActiveIndex((prev) => (prev >= total - 1 ? 0 : prev + 1));
  }, [total]);

  useEffect(() => {
    if (variant !== "v2" || total <= 1) return;
    const interval = window.setInterval(goNext, 4200);
    return () => window.clearInterval(interval);
  }, [goNext, total, variant]);

  const active = availableItems[activeIndex];

  const handleViewMore = () => {
    router.push("/app/buy-sell");
  };

  const priceLabel = (item: IBuySellItem) =>
    `$${Number(item.price ?? 0).toLocaleString("es-CL")}`;

  const conditionLabel = (condition?: string) => {
    if (condition === "NEW") return t("Nuevo");
    if (condition === "LIKE_NEW") return t("Como nuevo");
    if (condition === "REFURBISHED") return t("Reacondicionado");
    if (condition === "USED") return t("Usado");
    return "";
  };

  const ActiveSideCard = (
    variant === "v2" ? SideCardV2 : SideCard
  ) as typeof SideCard;

  const initialLoading = loading && !data;

  if (!isEnabled) {
    return null;
  }

  if (initialLoading) {
    return (
      <ActiveSideCard
        title={t("En venta")}
        loading
        variant={variant}
        collapsible={false}
        defaultExpanded
        icon={
          variant === "v2" ? (
            <ShoppingBag size={16} strokeWidth={1} color="#0073FF" />
          ) : null
        }
      >
        <span />
      </ActiveSideCard>
    );
  }

  // Auto-hide si no hay items disponibles.
  if (total === 0) return null;

  const sellerName = active?.seller
    ? [active.seller.name, active.seller.lastname].filter(Boolean).join(" ")
    : "";

  return (
    <>
      {/* Mismas keyframes que RecognitionCorporateValueRankingWidget. */}
      <style jsx global>{`
        @keyframes explore-ranking-person-enter {
          0% {
            opacity: 0;
            transform: translateY(14px) scale(0.94);
            filter: blur(8px);
          }
          58% {
            opacity: 1;
            transform: translateY(-2px) scale(1.018);
            filter: blur(0);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
            filter: blur(0);
          }
        }
        @keyframes explore-ranking-avatar-pop {
          0% {
            opacity: 0;
            transform: scale(0.72) rotate(-4deg);
          }
          68% {
            opacity: 1;
            transform: scale(1.07) rotate(1deg);
          }
          100% {
            opacity: 1;
            transform: scale(1) rotate(0deg);
          }
        }
        @keyframes explore-ranking-copy-rise {
          0% {
            opacity: 0;
            transform: translateY(7px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .explore-ranking-person-enter {
          animation: explore-ranking-person-enter 560ms
            cubic-bezier(0.22, 1, 0.36, 1) both;
          will-change: transform, opacity, filter;
        }
        .explore-ranking-person-enter .explore-ranking-avatar-pop {
          animation: explore-ranking-avatar-pop 620ms
            cubic-bezier(0.34, 1.56, 0.64, 1) 70ms both;
        }
        .explore-ranking-person-enter .explore-ranking-copy-rise {
          animation: explore-ranking-copy-rise 420ms
            cubic-bezier(0.22, 1, 0.36, 1) 150ms both;
        }
      `}</style>
      <ActiveSideCard
        title={t("En venta")}
        variant={variant}
        collapsible={false}
        defaultExpanded
        buttonText={total > 1 ? t("Ver todos") : ""}
        onViewMore={handleViewMore}
        icon={
          variant === "v2" ? (
            <ShoppingBag size={16} strokeWidth={1} color="#0073FF" />
          ) : null
        }
      >
        {variant === "v2" && active && (
          <div className="space-y-2.5">
            <div
              key={active.id}
              className="explore-ranking-person-enter relative px-3 py-2"
            >
              <div className="relative flex flex-col items-center text-center">
                {/* Imagen del producto — o fallback icónico */}
                <button
                  type="button"
                  onClick={handleViewMore}
                  className="explore-ranking-avatar-pop overflow-hidden rounded-2xl ring-2 ring-white/90 ring-offset-2 ring-offset-white/60 transition-transform hover:scale-[1.02] dark:ring-zinc-900 dark:ring-offset-zinc-800"
                  aria-label={active.title}
                >
                  {active.images?.[0] ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={active.images[0]}
                      alt={active.title}
                      className="h-24 w-24 object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-24 w-24 items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 dark:from-zinc-800 dark:to-zinc-900">
                      <ShoppingBag
                        className="h-10 w-10 text-slate-400 dark:text-zinc-500"
                        strokeWidth={1.4}
                      />
                    </div>
                  )}
                </button>

                <p
                  className="explore-ranking-copy-rise mt-2 line-clamp-2 max-w-full break-words px-2 text-[14px] font-semibold leading-tight text-slate-900 dark:text-zinc-100"
                  title={active.title}
                >
                  {active.title}
                </p>

                <LiquidPill
                  variant="emerald"
                  appearance="tinted"
                  size="xs"
                  text={priceLabel(active)}
                  className="explore-ranking-copy-rise mt-1"
                />

                {active.condition && conditionLabel(active.condition) && (
                  <span className="explore-ranking-copy-rise mt-1 text-[11px] text-slate-500 dark:text-zinc-400">
                    {conditionLabel(active.condition)}
                  </span>
                )}

                {active.seller &&
                  (sellerName || active.seller.profileImage) && (
                    <div className="explore-ranking-copy-rise mt-2 flex items-center gap-1.5 text-xs text-slate-500 dark:text-zinc-400">
                      <UserAvatar user={active.seller as any} size={18} />
                      <span className="max-w-[10rem] truncate">
                        {sellerName || t("Vendedor")}
                      </span>
                    </div>
                  )}
              </div>
            </div>
          </div>
        )}

        {variant === "default" && (
          <div className="my-2 space-y-2">
            {availableItems.slice(0, 3).map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={handleViewMore}
                className="flex w-full items-center gap-2.5 rounded-md p-1.5 text-left transition-colors hover:bg-slate-50 dark:hover:bg-zinc-800/50"
              >
                <div className="h-10 w-10 shrink-0 overflow-hidden rounded-md bg-slate-100 dark:bg-zinc-800">
                  {item.images?.[0] ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={item.images[0]}
                      alt={item.title}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <ShoppingBag className="h-4 w-4 text-slate-400" />
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-semibold text-slate-800 dark:text-zinc-200">
                    {item.title}
                  </p>
                  <p className="text-[11px] text-emerald-600 dark:text-emerald-400">
                    {priceLabel(item)}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </ActiveSideCard>
    </>
  );
};

export default BuySellWidget;
